import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import './../styles.css';

const Insights = () => {
  const [applications, setApplications] = useState([]);
  const [skillsFrequency, setSkillsFrequency] = useState({});
  const [skillsCompanies, setSkillsCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    setTimeout(() => {
      fetchApplications();
      setLoading(false);
    }, 1000);
  }, []);

  const fetchApplications = async () => {
    const url = `${process.env.REACT_APP_SRV_BASE_URL}/api/applications/${userId}`;
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setApplications(Array.isArray(data) ? data : []);
      calculateSkillsFrequency(Array.isArray(data) ? data : []);
      calculateSkillsCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching applications data:', error);
    }
  };

  // Function to capitalize the first letter of a string
  const capitalizeFirstLetter = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Function to calculate skills frequency across all applications
  const calculateSkillsFrequency = (applications) => {
    const skillsMap = {};
    let totalSkills = 0;
    const skillsPercentage = {};

    applications.forEach((application) => {
      application.skills.forEach((skill) => {
        // Capitalize the first letter of the skill
        const capitalizedSkill = capitalizeFirstLetter(skill);
        skillsMap[capitalizedSkill] = (skillsMap[capitalizedSkill] || 0) + 1;
        totalSkills++;
      });
    });

    for (let skill in skillsMap) {
      skillsPercentage[skill] = (skillsMap[skill] / totalSkills) * 100;
    }

    setSkillsFrequency(skillsPercentage);
  };

  // Function to calculate companies for each skill
  const calculateSkillsCompanies = (applications) => {
    const companies = {};
    applications.forEach((application) => {
      application.skills.forEach((skill) => {
        // Capitalize the first letter of the skill
        const capitalizedSkill = capitalizeFirstLetter(skill);
        if (!companies[capitalizedSkill]) {
          companies[capitalizedSkill] = [];
        }
        if (!companies[capitalizedSkill].includes(application.companyName)) {
          companies[capitalizedSkill].push(application.companyName);
        }
      });
    });
    setSkillsCompanies(companies);
  };

  // Function to generate color based on skill name
  const generateColor = (name) => {
    // Generate a color based on the hash of the skill name
    const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hue = hash % 360;
    const saturation = 50;
    const lightness = 50;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const generateGradientBackground = () => {
    let cumulativePercentage = 0;
    const gradientSegments = Object.keys(skillsFrequency).map((skill) => {
      const color = generateColor(skill);
      const start = cumulativePercentage;
      const end = cumulativePercentage + skillsFrequency[skill];
      cumulativePercentage = end;
      return `${color} ${start}% ${end}%`;
    });

    return `conic-gradient(${gradientSegments.join(", ")})`;
  };

  const calculateRotation = (index) => {
    let cumulativePercentage = 0;
    const skillKeys = Object.keys(skillsFrequency);
    for (let i = 0; i < index; i++) {
      cumulativePercentage += skillsFrequency[skillKeys[i]];
    }
    cumulativePercentage += skillsFrequency[skillKeys[index]] / 2;
    return cumulativePercentage * 3.6;
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <NavBar />
      <div className="content-container">
        {applications.length === 0 ? (
          <div>
          <p className="no-insights-message">
            <FontAwesomeIcon icon={faInfoCircle} /> No insights found
          </p>
          <span><Link to="/applications" className="no-insights-link">Start tracking applications to gain insights</Link></span>
        </div>
        ) : (
          <>
            <div className="skills-container">
              <div
                className="pie-chart"
                style={{ background: generateGradientBackground() }}
              >
                {Object.keys(skillsFrequency).map((skill, index) => (
                  <li
                    key={skill}
                    style={{
                      transform: `rotate(calc(-90deg + ${calculateRotation(
                        index
                      )}deg))`,
                    }}
                  >
                    {`${skill} ${skillsFrequency[skill].toFixed(2)}%`}
                  </li>
                ))}
              </div>
              <div className="skills-section">
                {Object.keys(skillsFrequency).map((skill, index) => (
                  <div key={skill + index} className="skill-container">
                    <div
                      className="skill-box"
                      style={{ backgroundColor: generateColor(skill) }}
                    ></div>
                    <p>
                      {skill} is listed in {skillsFrequency[skill].toFixed(2)}% of
                      your applications
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <hr className="line" />
            <div className="skills-sidebar">
              <div className="skills-section">
                {Object.keys(skillsCompanies).map((skill, index) => (
                  <div key={skill + index} className="skill-container">
                    <div
                      className="skill-box"
                      style={{ backgroundColor: generateColor(skill) }}
                    ></div>
                    <p className="skills-text">
                      {skill} is listed by {skillsCompanies[skill].join(", ")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Insights;
